import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { articleContents } from "@/mocks/articleContents";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://hipobuy2026.net";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const content = articleContents[slug || ""];

  useEffect(() => {
    const cleanup = () => {
      document.querySelectorAll('script[data-schema="article"]').forEach((s) => s.remove());
      document.querySelectorAll('meta[data-article-seo]').forEach((m) => m.remove());
      const titleEl = document.querySelector('title');
      if (titleEl) titleEl.textContent = "HipoBuy 2026 – Premium Fashion Shopping | hipobuy2026.net";
      const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (canonical) canonical.href = `${SITE_URL}/`;
    };

    if (!article) return cleanup;

    // Update title
    const titleEl = document.querySelector('title');
    if (titleEl) titleEl.textContent = `${article.title} | HipoBuy 2026`;

    // Update canonical
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) canonical.href = `${SITE_URL}/${article.slug}`;

    // Remove old article meta
    document.querySelectorAll('meta[data-article-seo]').forEach((m) => m.remove());

    // Add meta description
    const metaDesc = document.createElement("meta");
    metaDesc.name = "description";
    metaDesc.content = article.excerpt;
    metaDesc.setAttribute("data-article-seo", "true");
    document.head.appendChild(metaDesc);

    // Add OG tags
    const ogTags: Record<string, string> = {
      "og:title": `${article.title} | HipoBuy 2026`,
      "og:description": article.excerpt,
      "og:url": `${SITE_URL}/${article.slug}`,
      "og:image": article.image,
      "og:type": "article",
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", property);
      meta.content = content;
      meta.setAttribute("data-article-seo", "true");
      document.head.appendChild(meta);
    });

    // Schema.org JSON-LD
    document.querySelectorAll('script[data-schema="article"]').forEach((s) => s.remove());

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.image,
      "datePublished": article.date,
      "dateModified": article.date,
      "author": {
        "@type": "Organization",
        "name": "HipoBuy 2026",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "HipoBuy 2026",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/751042991eee80e3d0f5ce87d83ca658/78871cc3cb98add911b4c909beefd628.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${SITE_URL}/${article.slug}`
      },
      "articleSection": article.category,
      "keywords": `hipobuy, hipobuy 2026, ${article.category.toLowerCase()}, premium fashion`
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": article.category,
          "item": `${SITE_URL}/${article.slug}`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": article.title,
          "item": `${SITE_URL}/${article.slug}`
        }
      ]
    };

    [articleSchema, breadcrumbSchema].forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-schema", "article");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return cleanup;
  }, [article, slug]);

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/" className="text-amber-500 hover:underline cursor-pointer">Back to Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <div className="relative h-64 md:h-80 overflow-hidden">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-4 md:px-6 pb-8">
            <span className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full mb-3">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-3xl font-black text-white leading-tight">{article.title}</h1>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-4 pb-2">
          <nav aria-label="breadcrumb">
            <ol className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
              <li><Link to="/" className="hover:text-amber-500 transition-colors cursor-pointer">Home</Link></li>
              <li><span className="mx-1">/</span></li>
              <li><span className="text-gray-400">{article.category}</span></li>
              <li><span className="mx-1">/</span></li>
              <li className="text-gray-700 font-medium truncate max-w-xs">{article.title}</li>
            </ol>
          </nav>
        </div>

        {/* Meta */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></div>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line"></i></div>
              {article.readTime}
            </span>
            <a
              href="https://repsw2c.com/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="ml-auto inline-flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-3 h-3 flex items-center justify-center"><i className="ri-shopping-bag-line"></i></div>
              Buy It Now
            </a>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <article className="lg:col-span-2 prose-custom">
              <div
                className="text-gray-700 text-sm leading-relaxed space-y-5"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </article>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* CTA */}
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
                <h3 className="text-base font-black text-gray-900 mb-2">Shop on HipoBuy 2026</h3>
                <p className="text-gray-600 text-xs mb-4 leading-relaxed">
                  Find premium fashion at the best prices. Fast shipping worldwide.
                </p>
                <a
                  href="https://repsw2c.com/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="block text-center px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-full transition-all cursor-pointer whitespace-nowrap"
                >
                  Buy It Now
                </a>
                <a
                  href="https://repsw2c.com/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="block text-center px-4 py-2.5 border-2 border-gray-900 text-gray-900 text-sm font-bold rounded-full transition-all cursor-pointer whitespace-nowrap mt-2 hover:bg-gray-900 hover:text-white"
                >
                  HipoBuy Shipping
                </a>
              </div>

              {/* Related */}
              <div>
                <h3 className="text-sm font-black text-gray-900 mb-4 uppercase tracking-wider">Related Articles</h3>
                <div className="space-y-3">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/${r.slug}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden">
                        <img src={r.image} alt={r.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div>
                        <span className="text-amber-500 text-xs font-semibold">{r.category}</span>
                        <p className="text-xs font-bold text-gray-800 leading-snug group-hover:text-amber-600 transition-colors line-clamp-2">{r.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Back */}
              <Link
                to="/"
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></div>
                Back to Home
              </Link>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
